import _ from "lodash"
import { ImmutableBehaviorSubject as BehaviorSubject } from "data/lib/immutable-behavior-subject"

import { push_undo } from 'data/lib/undo'
import { handleReferences } from 'data/lib/handle-references'
import Client from "data/_client"
import { ApiResult } from "data/interface/api"
import notificationService from "data/notification"
import incrementUnique from 'data/lib/increment-unique'
import { diff_obj } from 'data/lib/diff'

import { alphanum } from "components/table/lib/sort"
import { PromiseWatch } from "./lib/promise-helpers"

export type RecoveryGroup = {
  recoverygroupid: number
  propertyid: number
  code: string
  systeminput?: string
}
// private module logic //

const id: string = "recoveryGroup"
const initialState: ApiResult<RecoveryGroup[]> = { id, state: "init", value: [] }
const recoveryGroupStream = new BehaviorSubject<ApiResult<RecoveryGroup[]>>(initialState)
const recoveryGroupEventStream = new BehaviorSubject<{ type: string; payload: any }>({
  type: '',
  payload: {}
})

let recoveryGroupMap = {}
const _fetches_ = PromiseWatch()
const _patches_ = PromiseWatch()

function clear(): void {
  recoveryGroupStream.next({ id, state: 'ok', value: [] })
  recoveryGroupMap = {}
}

function get(recoverygroupid: number): RecoveryGroup {
  return recoveryGroupMap[recoverygroupid]
}

function copy(propertyid:number, recoverygroupid:number, code:string) {
  const currentRecoveryGroupVal = recoveryGroupStream.getValue().value;
  recoveryGroupStream.next({ id, state: 'busy', value: currentRecoveryGroupVal });

  const payload = {table: 'recoverygroup', recordid: recoverygroupid, _synchronous: true, code: incrementUnique(code, "code", currentRecoveryGroupVal)};

  Client.post(`msg/svc_recordcopy`, payload)
    .then((recoveryGroup: RecoveryGroup) => {
      const newRecord = recoveryGroup[0]
      push_undo({
        undofunc: () => deleteOne(propertyid, newRecord.recoverygroupid),
        redofunc: () => copy(propertyid, recoverygroupid, code)
      });
      recoveryGroupMap[newRecord.recoverygroupid] = newRecord
      // merge results unique by recoverygroupid
      const currentRecoveryGroupVal = recoveryGroupStream.getValue().value
      recoveryGroupStream.next({
        id,
        state: 'ok',
        value: _.unionBy([newRecord], currentRecoveryGroupVal, 'recoverygroupid').sort((a, b) => alphanum(a.code, b.code))
      })
      recoveryGroupEventStream.next({ type: 'RECOVERYGROUP_COPIED', payload: newRecord })
      return newRecord
    })
    .catch((err: { error: {text: string }}) => {
      // on error overwrite optimistic update
      recoveryGroupStream.next({ id, state: 'error', error: err, value: currentRecoveryGroupVal })
      notificationService.notify({ type: 'danger', message: err.error.text, dismiss: 5000 })
      fetchRecoveryGroups(propertyid)
      return err
    })
}

function copies(propertyid:number, recoverygroupid:number, count) {
  const currentRecoveryGroupVal = recoveryGroupStream.getValue().value;
  recoveryGroupStream.next({ id, state: 'busy', value: currentRecoveryGroupVal });

  const payload = {table: 'recoverygroup', recordid: recoverygroupid, _synchronous: true, unique: true, count};

  return Client.post(`msg/svc_recordcopy`, payload)
    .then((recoverygroups: RecoveryGroup[]) => {
      push_undo({
        undofunc: () => deleteMultiple(propertyid, _.map(recoverygroups, 'recoverygroupid')),
        redofunc: () => copies(propertyid, recoverygroupid, count)
      });
      _.forEach(recoverygroups, newRecord => recoveryGroupMap[newRecord.recoverygroupid] = newRecord)
      // merge results unique by recoverygroupid
      const currentRecoveryGroupVal = recoveryGroupStream.getValue().value
      recoveryGroupStream.next({
        id,
        state: 'ok',
        value: _.unionBy(recoverygroups, currentRecoveryGroupVal, 'recoverygroupid').sort((a, b) => alphanum(a.code, b.code))
      })
      recoveryGroupEventStream.next({ type: 'RECOVERYGROUP_COPIED', payload: recoverygroups })
      return recoverygroups
    })
    .catch((err: { error: {text: string }}) => {
      // on error overwrite optimistic update
      recoveryGroupStream.next({ id, state: 'error', error: err, value: currentRecoveryGroupVal })
      notificationService.notify({ type: 'danger', message: err.error.text, dismiss: 5000 })
      fetchRecoveryGroups(propertyid)
      return err
    })
}

function create(propertyid: number): Promise<RecoveryGroup | { message: string }> {
  const currentValue = recoveryGroupStream.getValue().value
  recoveryGroupStream.next({ id, state: 'busy', value: currentValue })

  const payload = {
    propertyid,
    recoverygroupdetail: {
      items: [{recoveryNamedItemId: ""}],
      state: "detailed"
    }
  }

  return Client.post(`recoverygroup`, payload).then((recoveryGroups: RecoveryGroup) => {
    push_undo({
      undofunc: () => deleteOne(propertyid, recoveryGroups.recoverygroupid),
      redofunc: () => create(propertyid)
    })
    recoveryGroupMap[recoveryGroups.recoverygroupid] = recoveryGroups
    // merge results unique by recoverygroupid
    const currentValue = recoveryGroupStream.getValue().value
    recoveryGroupStream.next({
      id,
      state: 'ok',
      value: _.unionBy([recoveryGroups], currentValue, 'recoverygroupid')
    })
    recoveryGroupEventStream.next({ type: 'RECOVERYGROUP_CREATED', payload: recoveryGroups })
    return recoveryGroups
  })
  .catch((err: { message: string }) => {
    // on error overwrite optimistic update
    recoveryGroupStream.next({ id, state: 'error', error: err, value: currentValue })
    notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
    fetchRecoveryGroups(propertyid)
    return err
  })
}

async function deleteOne(
  propertyid: number,
  recoverygroupid: number
): Promise<boolean | { message: string }> {
  const currentValue = recoveryGroupStream.getValue().value

  const deleteFn = () => deleteOne(propertyid, recoverygroupid)
  const refreshFn = () => fetchRecoveryGroups(propertyid)
  if (!(await handleReferences('recoverygroup', recoverygroupid, {deleteFn, refreshFn}))) {
    return false
  }

  // filter out the deleted recoverygroupid
  const value = _(currentValue)
    .filter((p) => p.recoverygroupid !== recoverygroupid)
    .value()
  value.forEach((p) => (recoveryGroupMap[p.recoverygroupid] = p))

  recoveryGroupStream.next({ id, state: 'ok', value })

  // update the db
  return _patches_.all(recoverygroupid).then(() =>
  Client.del(`recoverygroup?recoverygroupid=eq.${recoverygroupid}`)
    .then((recoveryGroups: RecoveryGroup) => {
      delete recoveryGroupMap[recoveryGroups.recoverygroupid]
      push_undo({
        undofunc: () => {
          Client.post('recoveryGroups', recoveryGroups)
          recoveryGroupMap[recoveryGroups.recoverygroupid] = recoveryGroups
          recoveryGroupStream.next({ id, state: 'ok', value: currentValue })
        },
        redofunc: () => deleteOne(propertyid, recoverygroupid)
      })
      // update already happened optimistically
      return true
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      recoveryGroupStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({
        type: 'danger',
        message: err.message,
        dismiss: 5000
      })
      fetchRecoveryGroups(propertyid)
      return err
    })
  )
}

async function deleteMultiple(
  propertyid: number,
  recoverygroupids: number[]
): Promise<boolean | { message: string }> {
  const currentValue = recoveryGroupStream.getValue().value

  const deleteFn = (ids) => deleteMultiple(propertyid, ids)
  const refreshFn = () => fetchRecoveryGroups(propertyid)
  if (!(await handleReferences('recoverygroup', recoverygroupids, {deleteFn, refreshFn}))) {
    return false
  }

  // filter out the deleted recoverygroupid
  const value = _(currentValue)
    .filter((p) => !recoverygroupids.includes(p.recoverygroupid))
    .value()
  value.forEach((p) => (recoveryGroupMap[p.recoverygroupid] = p))

  recoveryGroupStream.next({ id, state: 'ok', value })

  // update the db
  return _patches_.all(recoverygroupids).then(() =>
  Client.del(`recoverygroup?recoverygroupid=in.${recoverygroupids}`)
    .then((recoveryGroups: RecoveryGroup[]) => {
      recoveryGroups.forEach(g => delete recoveryGroupMap[g.recoverygroupid])
      push_undo({
        undofunc: () => {
          Client.post('recoveryGroups', recoveryGroups)
          recoveryGroups.forEach(g => recoveryGroupMap[g.recoverygroupid] = g)
          recoveryGroupStream.next({ id, state: 'ok', value: currentValue })
        },
        redofunc: () => deleteMultiple(propertyid, recoverygroupids)
      })
      // update already happened optimistically
      return true
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      recoveryGroupStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({
        type: 'danger',
        message: err.message,
        dismiss: 5000
      })
      fetchRecoveryGroups(propertyid)
      return err
    })
  )
}

function fetchRecoveryGroups(
  propertyId: number,
  limit: number = 0,
  offset: number = 0
): Promise<RecoveryGroup[] | { message: string }> {
  if (_fetches_.has(propertyId)) return _fetches_.first(propertyId)

  clear()
  recoveryGroupStream.next({ id, state: 'busy', value: recoveryGroupStream.getValue().value })

  let url = `recoverygroup?propertyid=eq.${propertyId}&order=type.asc,code.asc.nullsfirst`
  if (limit && offset >= 0) {
    url = `recoverygroup?propertyid=eq.${propertyId}&limit=${limit}&offset=${offset}&order=type.asc,code.asc.nullsfirst`
  }

  return _fetches_.watch(propertyId, Client.get(url)
  .then((records: RecoveryGroup[]) => {
    _.forEach(records, (recoveryGroups) => (recoveryGroupMap[recoveryGroups.recoverygroupid] = recoveryGroups))
    // merge results unique by recoverygroupid
    recoveryGroupStream.next({ id, state: 'ok', value: records })
    return records
  })
  .catch((err: { message: string }) => {
    notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
    return err
  }))
}

function patch(recoveryGroup: RecoveryGroup): Promise<RecoveryGroup | { message: string }> {
  const currentValue = recoveryGroupStream.getValue().value
  const recoverygroupid: number = recoveryGroup.recoverygroupid
  const currentRecoveryGroups = _.find(currentValue, { recoverygroupid })
  const changeset = diff_obj(recoveryGroup, currentRecoveryGroups)
  if (_.isEmpty(changeset)) return new Promise(() => currentValue)
  push_undo({
    undofunc: () => patch(currentRecoveryGroups),
    redofunc: () => patch(recoveryGroup)
  })

  // optimistic update
  recoveryGroupStream.next({
    id,
    state: 'ok',
    value: _.set(_.cloneDeep(currentValue), _.findIndex(currentValue, {recoverygroupid}), recoveryGroup)
  })

  return _patches_.watch(recoverygroupid, Client.patch(`recoverygroup?recoverygroupid=eq.${recoverygroupid}`, changeset)
    .then((mf: RecoveryGroup[]) => {
      _.forEach(mf, (recoveryGroup) => (recoveryGroupMap[recoveryGroup.recoverygroupid] = recoveryGroup))
      return mf[0]
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      recoveryGroupStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
      const propertyid = _.get(currentRecoveryGroups, 'propertyid')
      if (propertyid) fetchRecoveryGroups(propertyid)
      return err
    }))
}

// public module interface //

const recoveryGroupsService = {
  stream: () => recoveryGroupStream.asObservable(),
  value: () => recoveryGroupStream.getValue().value,
  eventStream: () => recoveryGroupEventStream.asObservable(),
  copy,
  copies,
  clear,
  create,
  deleteOne,
  deleteMultiple,
  get,
  fetchRecoveryGroups,
  patch
}

export default recoveryGroupsService
